/**
 * The basic purpose of this class is to
 * manage a memory cache for the tokens
 * and user data. This should make the fetch
 * much faster and also allow us to inject this
 * in the create base model hook.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  GenericProfile,
  IUserRolesAndPermissions,
  UserProfile,
  UserToken,
} from '../types/user';

import { Region } from '@taleemabad/dsm/components';

class UserCache {
  private tokenCache: UserToken | null = null;
  private userCache: UserProfile | null = null;
  private deviceId: string | null = null;
  private regionCache: Region | null = null;
  private appId: string | null = null;
  private activeRolePermission: IUserRolesAndPermissions | null = null;

  getDeviceId(): string {
    return this.deviceId || '';
  }

  setDeviceId(deviceId: string | null): void {
    this.deviceId = deviceId;
  }

  getTokenFromCache(): UserToken | null {
    return this.tokenCache;
  }

  setTokenToCache(token: UserToken): void {
    this.tokenCache = token;
  }

  getRegionFromCache(): Region | null {
    return this.regionCache;
  }

  setRegionToCache(region: Region): void {
    this.regionCache = region;
  }

  getUserFromCache(): UserProfile | null {
    return this.userCache;
  }

  async setUserToCache(user: UserProfile) {
    if (process.env.NODE_ENV !== 'production')
      console.log('Setting userCache', user);
    this.userCache = user;
  }

  getActiveProfile(): GenericProfile {
    return this.userCache?.profiles.find(
      (profile) => profile.isSelected,
    ) as GenericProfile;
  }
  setActiveRolePermission(rolePermission: IUserRolesAndPermissions): void {
    this.activeRolePermission = rolePermission;
  }
  getActiveRolePermission(): IUserRolesAndPermissions | null {
    return this.activeRolePermission;
  }

  resetCache(): void {
    this.tokenCache = null;
    this.userCache = null;
    this.activeRolePermission = null;
  }

  // Technical Debt: When expanding tenants, this should be accompanied with a Backend API that fetches tenants.
  async getBaseUrl(): Promise<string | null> {
    const selectedRegion = this.getRegionFromCache();
    // Use Browser URL for Web App mode
    if (
      process.env.NX_PUBLIC_DEBUG === 'false' &&
      !Capacitor.isNativePlatform()
    ) {
      return window.location.origin;
    }

    // Native Build for Taleemabad (School App) use MH Url
    if (await this.isTaleemabadApp()) {
      return process.env.NX_PUBLIC_MH_URL ?? null;
    }

    // For Native Build for NIETE, Selected tenant url.
    if (Capacitor.isNativePlatform()) {
      return selectedRegion?.baseUrl ?? null;
    }
    return null;
  }

  async getAppId(): Promise<string | null> {
    if (Capacitor.isNativePlatform() && !this.appId) {
      await this.setAppId();
    }
    return this.appId;
  }

  private async setAppId(): Promise<void> {
    const { id } = await App.getInfo();
    this.appId = id;
  }

  async isTaleemabadApp(): Promise<boolean> {
    const appId = await this.getAppId();
    return appId?.includes('taleemabad') ?? false;
  }
}

export const userCache = new UserCache();
